import React, { useState } from 'react';
import './ResponsePattern.css';

// Inline icons (feather outlines, as in the design) — no icon package so the
// primitives load in every test environment without ESM transforms.
const svgProps = (size, stroke = 2) => ({
  width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: stroke, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true,
});
const Check = ({ size = 15, strokeWidth = 2.2, className }) => (
  <svg {...svgProps(size, strokeWidth)} className={className}><polyline points="20 6 9 17 4 12" /></svg>
);
const ChevronDown = ({ size = 16, className }) => (
  <svg {...svgProps(size)} className={className}><polyline points="6 9 12 15 18 9" /></svg>
);
const AlertTriangle = ({ size = 15 }) => (
  <svg {...svgProps(size)}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
);
const Info = ({ size = 15 }) => (
  <svg {...svgProps(size)}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
);
const Clock = ({ size = 17 }) => (
  <svg {...svgProps(size)}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
);
const TrendingUp = ({ size = 17 }) => (
  <svg {...svgProps(size)}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>
);
const X = ({ size = 15, strokeWidth = 2.2 }) => (
  <svg {...svgProps(size, strokeWidth)}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
);

/**
 * AI Response Pattern — the shared anatomy every assistant turn is built from.
 * (design: "AI Response Pattern.dc.html")
 *
 *   1 · Answer first            <Headline>
 *   2 · Caveats before numbers  <Caveat>
 *   3 · One signal strip        <SignalStrip>
 *   4 · Sections, collapsed     <Section>  (+ <Recommendations>, <Sources>)
 *   5 · Action last, summarised <ActionCard> (+ <ActionFooter>, <ActionDone>)
 *
 * Free-form chat turns get here through parseResponsePattern + ResponseBody;
 * workflow runs and the approval cards compose these directly. Keep visual
 * decisions in ResponsePattern.css — these components carry structure only.
 */

export function Headline({ children, as: Tag = 'div', className = '' }) {
  if (!children) return null;
  return <Tag className={`rp-headline ${className}`.trim()}>{children}</Tag>;
}

/** tone: 'warning' (default) | 'info' | 'error' */
export function Caveat({ children, tone = 'warning' }) {
  if (!children) return null;
  const Icon = tone === 'info' ? Info : AlertTriangle;
  return (
    <div className={`rp-caveat${tone === 'info' ? ' is-info' : tone === 'error' ? ' is-error' : ''}`} role="note">
      <Icon size={15} strokeWidth={2} />
      <span>{children}</span>
    </div>
  );
}

/**
 * signals: [{ label, value, sub, tone }] — tone: 'accent'|'positive'|'negative'|'muted'
 * Three to five figures, each with its own denominator in `sub`. Capped at 5;
 * fewer than 2 renders nothing (one number is a headline, not a strip).
 */
export function SignalStrip({ signals }) {
  const list = (Array.isArray(signals) ? signals : [])
    .filter(s => s && (s.value != null && String(s.value).trim() !== ''))
    .slice(0, 5);
  if (list.length < 2) return null;
  return (
    <div className="rp-signals" role="list">
      {list.map((s, i) => (
        <div key={i} className={`rp-signal${s.tone ? ` tone-${s.tone}` : ''}`} role="listitem">
          <div className="rp-signal-label" title={s.label}>{s.label}</div>
          <div className="rp-signal-value" title={String(s.value)}>{s.value}</div>
          {s.sub ? <div className="rp-signal-sub" title={s.sub}>{s.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

/**
 * One collapsed section: label · count · one-line preview · hint · chevron.
 * Controlled when `open`/`onToggle` are given, otherwise self-managed.
 */
export function Section({
  label,
  count = null,
  countTone = null,
  preview = null,
  hint = null,
  children,
  open: openProp,
  onToggle,
  defaultOpen = false,
  flush = false,
  bodyClassName = '',
  id,
}) {
  const [openState, setOpenState] = useState(defaultOpen);
  const controlled = typeof openProp === 'boolean';
  const open = controlled ? openProp : openState;
  const toggle = () => {
    if (onToggle) onToggle(!open);
    if (!controlled) setOpenState(o => !o);
  };
  return (
    <div className={`rp-section${open ? ' is-open' : ''}`} data-section={id || undefined}>
      <button type="button" className="rp-section-head" onClick={toggle} aria-expanded={open}>
        <span className="rp-section-label">{label}</span>
        {count != null && count !== '' && (
          <span className={`rp-section-count${countTone === 'accent' ? ' is-accent' : ''}`}>{count}</span>
        )}
        <span className="rp-section-preview">{preview || ''}</span>
        {hint && <span className="rp-section-hint">{hint}</span>}
        <ChevronDown size={16} className="rp-section-chevron" />
      </button>
      {open && (
        <div className={`rp-section-body${flush ? ' is-flush' : ''} ${bodyClassName}`.trim()}>
          {children}
        </div>
      )}
    </div>
  );
}

export function Sections({ children }) {
  const kids = React.Children.toArray(children).filter(Boolean);
  if (!kids.length) return null;
  return <div className="rp-sections">{kids}</div>;
}

/** items: [{ title, body }] — numbered 01 / 02 / 03 */
export function Recommendations({ items }) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="rp-recs">
      {list.map((it, i) => (
        <div className="rp-rec" key={i}>
          <div className="rp-rec-head">
            <span className="rp-rec-num">{String(i + 1).padStart(2, '0')}</span>
            <span className="rp-rec-title">{it.title}</span>
          </div>
          {it.body ? <div className="rp-rec-body">{it.body}</div> : null}
        </div>
      ))}
    </div>
  );
}

/** lines: React nodes or strings — one per source / assumption */
export function Sources({ lines }) {
  const list = (lines || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="rp-sources">
      {list.map((line, i) => <div key={i}>{line}</div>)}
    </div>
  );
}

/** Chart · Table segmented control */
export function SegmentedToggle({ value, options, onChange }) {
  return (
    <span className="rp-seg" role="tablist">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={value === opt.value}
          className={value === opt.value ? 'is-on' : ''}
          onClick={() => onChange(opt.value)}
        >{opt.label}</button>
      ))}
    </span>
  );
}

// ── 5 · Action card ────────────────────────────────────────────────────────

const ACTION_ICONS = { clock: Clock, trend: TrendingUp, check: Check };

/** state: 'pending' | 'done' | 'busy' | 'muted' | 'error' */
export function StatusPill({ state = 'pending', children }) {
  const cls = state === 'done' ? 'is-done' : state === 'busy' ? 'is-busy' : state === 'muted' ? 'is-muted' : state === 'error' ? 'is-error' : 'is-pending';
  return <span className={`rp-pill ${cls}`}>{children}</span>;
}

/**
 * The action card shell. Header (icon · title · mono sub · pill), optional
 * chips row with a "View full instructions" disclosure, any body, then a
 * footer (pending) or a done banner. Consumers pass the slots; the shell owns
 * the layout so every workflow's action reads the same.
 */
export function ActionCard({
  icon = 'clock',
  iconNode = null,
  title,
  sub = null,
  pill = null,           // { state, label }
  chips = [],            // strings or nodes
  disclosure = null,     // { label, content }
  note = null,
  error = null,
  children,              // body (tables, rows) — rendered between chips and footer
  bodyPadded = false,
  footer = null,         // node — rendered inside .rp-action-foot
  footerMeta = null,     // right-aligned mono text in the footer
  done = null,           // { tone: 'success'|'muted'|'error', text }
  destructive = false,
  className = '',
}) {
  const [discOpen, setDiscOpen] = useState(false);
  const Icon = iconNode ? null : (ACTION_ICONS[icon] || Clock);
  const hasChips = (chips && chips.length > 0) || !!disclosure;
  return (
    <div className={`rp-action${destructive ? ' is-destructive' : ''} ${className}`.trim()}>
      <div className="rp-action-head">
        <span className="rp-action-icon">{iconNode || <Icon size={17} strokeWidth={2} />}</span>
        <div className="rp-action-titles">
          <div className="rp-action-title">{title}</div>
          {sub ? <div className="rp-action-sub" title={typeof sub === 'string' ? sub : undefined}>{sub}</div> : null}
        </div>
        {pill ? <StatusPill state={pill.state}>{pill.label}</StatusPill> : null}
      </div>
      {hasChips && (
        <div className="rp-action-chips">
          {(chips || []).filter(Boolean).map((c, i) => (
            typeof c === 'string' ? <span key={i} className="rp-chip" title={c}>{c}</span> : <React.Fragment key={i}>{c}</React.Fragment>
          ))}
          {disclosure && (
            <button type="button" className="rp-chip-btn" onClick={() => setDiscOpen(o => !o)} aria-expanded={discOpen}>
              {discOpen ? (disclosure.closeLabel || 'Hide instructions') : (disclosure.label || 'View full instructions')}
            </button>
          )}
        </div>
      )}
      {disclosure && discOpen && (
        <div className="rp-action-disclosure">{disclosure.content}</div>
      )}
      {note ? <div className="rp-action-note">{note}</div> : null}
      {children ? <div className={`rp-action-body${bodyPadded ? ' is-padded' : ''}`}>{children}</div> : null}
      {error ? <div className="rp-action-error">{error}</div> : null}
      {done ? (
        <div className={`rp-action-done${done.tone === 'muted' ? ' is-muted' : done.tone === 'error' ? ' is-error' : ''}`}>
          {done.tone === 'error' ? <X size={15} strokeWidth={2.2} /> : <Check size={15} strokeWidth={2.2} />}
          <span>{done.text}</span>
        </div>
      ) : footer ? (
        <div className="rp-action-foot">
          {footer}
          {footerMeta ? <span className="rp-action-foot-meta">{footerMeta}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

/** variant: 'primary' | 'secondary' | 'ghost' | 'danger' */
export function Button({ variant = 'secondary', size, className = '', children, ...rest }) {
  return (
    <button
      type="button"
      className={`rp-btn rp-btn-${variant}${size === 'sm' ? ' rp-btn-sm' : ''} ${className}`.trim()}
      {...rest}
    >{children}</button>
  );
}

export function Checkbox({ on, onToggle, disabled = false, label }) {
  return (
    <button
      type="button"
      className={`rp-check${on ? ' is-on' : ''}`}
      aria-pressed={!!on}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
    >
      {on ? <Check size={12} strokeWidth={3.2} /> : null}
    </button>
  );
}

/** Compact key/value list for card details */
export function KeyValueList({ rows }) {
  const list = (rows || []).filter(r => r && r.length >= 2);
  if (!list.length) return null;
  return (
    <dl className="rp-kv">
      {list.map(([k, v], i) => (
        <React.Fragment key={i}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
